<template>
  <v-dialog
    :value="openModal"
    max-width="900px"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-receipt</v-icon>
        Dados da Ordem de Faturamento
      </v-card-title>

      <v-card-text class="pa-4">
        <v-form ref="form" v-model="valid">
          <!-- Forma de Pagamento -->
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="formData.paymentMethod"
                :items="paymentMethods"
                label="Forma de Pagamento *"
                :rules="[rules.paymentMethodRequired]"
                multiple
                chips
                outlined
                dense
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="formData.billingType"
                :items="billingTypes"
                label="Tipo de Faturamento *"
                :rules="rules.required"
                outlined
                dense
                @change="onBillingTypeChange"
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.total"
                label="Total *"
                :rules="rules.required"
                outlined
                dense
                prefix="R$"
                type="number"
                step="0.01"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Fatura Única -->
          <template v-if="formData.billingType === 'single'">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.dueDate"
                  label="Vencimento (Data) *"
                  type="date"
                  :rules="rules.required"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
          </template>

          <!-- Fatura Parcelada -->
          <template v-if="formData.billingType === 'installments'">
            <v-divider class="my-4"></v-divider>
            <h3 class="text-h6 mb-3">Configuração de Parcelas</h3>
            
            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  v-model="formData.entryType"
                  :items="entryTypes"
                  label="Tipo de Entrada"
                  outlined
                  dense
                  @change="onEntryTypeChange"
                ></v-select>
              </v-col>
              <v-col cols="12" md="4" v-if="formData.entryType !== 'none'">
                <v-text-field
                  v-if="formData.entryType === 'percentage'"
                  v-model="formData.entryPercentage"
                  label="Entrada (%) *"
                  :rules="[rules.entryPercentage]"
                  outlined
                  dense
                  suffix="%"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  @input="generateInstallments"
                ></v-text-field>
                <v-text-field
                  v-else-if="formData.entryType === 'value'"
                  v-model="formData.entryValue"
                  label="Entrada (R$) *"
                  :rules="[rules.entryValue]"
                  outlined
                  dense
                  prefix="R$"
                  type="number"
                  step="0.01"
                  min="0"
                  @input="generateInstallments"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4" v-else>
                <v-text-field
                  v-model="formData.numberOfInstallments"
                  label="Número de Parcelas *"
                  :rules="[rules.numberOfInstallments]"
                  outlined
                  dense
                  type="number"
                  min="1"
                  @input="onNumberOfInstallmentsChange"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4" v-if="formData.entryType !== 'none'">
                <v-text-field
                  :value="1"
                  label="Número de Parcelas *"
                  :rules="[rules.numberOfInstallments]"
                  outlined
                  dense
                  type="number"
                  readonly
                  hint="Com entrada, apenas 1 parcela é permitida"
                  persistent-hint
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row v-if="formData.entryType && formData.entryType !== 'none'">
              <v-col cols="12">
                <v-card outlined>
                  <v-card-title class="subtitle-1">
                    Resumo do Pagamento
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" md="4">
                        <div class="text-caption grey--text">Valor Total</div>
                        <div class="text-h6 font-weight-bold">{{ formatCurrency(formData.total) }}</div>
                      </v-col>
                      <v-col cols="12" md="4">
                        <div class="text-caption grey--text">Entrada</div>
                        <div class="text-h6 font-weight-bold success--text">{{ formatCurrency(calculatedEntry) }}</div>
                      </v-col>
                      <v-col cols="12" md="4">
                        <div class="text-caption grey--text">Valor Restante</div>
                        <div class="text-h6 font-weight-bold">{{ formatCurrency(remainingValue) }}</div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>
            <h3 class="text-h6 mb-3">Parcelas</h3>

            <v-card
              v-for="(installment, index) in formData.installments"
              :key="index"
              outlined
              class="mb-3"
            >
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="3">
                    <div class="text-caption grey--text">
                      {{ installment.type === 'entry' ? 'Entrada' : `Parcela ${installment.number || index}` }}
                    </div>
                    <div class="text-body-1 font-weight-bold" :class="installment.type === 'entry' ? 'success--text' : ''">
                      {{ formatCurrency(installment.value) }}
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="installment.dueDate"
                      :label="`Vencimento ${installment.type === 'entry' ? 'Entrada' : 'Parcela ' + (installment.number || index)} *`"
                      type="date"
                      :rules="rules.required"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3" v-if="index > 0 && installment.type !== 'entry' && formData.entryType === 'none'">
                    <v-btn
                      icon
                      small
                      color="error"
                      @click="removeInstallment(index)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </template>

          <v-divider class="my-4"></v-divider>

          <!-- Dados do Usuário -->
          <h3 class="text-h6 mb-3">Dados do Usuário</h3>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.user.name"
                label="Nome *"
                :rules="rules.required"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.user.email"
                label="Email *"
                :rules="[...rules.required, ...rules.email]"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="formData.user.userType"
                :items="userTypes"
                label="Tipo de Usuário *"
                :rules="rules.required"
                outlined
                dense
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-if="formData.user.userType === 'physical'"
                v-model="formData.user.cpf"
                label="CPF *"
                v-mask="'###.###.###-##'"
                :rules="rules.required"
                outlined
                dense
              ></v-text-field>
              <v-text-field
                v-else-if="formData.user.userType === 'legal'"
                v-model="formData.user.cnpj"
                label="CNPJ *"
                v-mask="'##.###.###/####-##'"
                :rules="rules.required"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.user.nameResponsible"
                label="Nome do Responsável"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.user.cpfResponsible"
                label="CPF do Responsável"
                v-mask="'###.###.###-##'"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.user.emailResponsible"
                label="Email do Responsável"
                :rules="rules.email"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.user.phoneResponsible"
                label="Telefone do Responsável"
                v-mask="'(##) #####-####'"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.user.stateTaxNumber"
                label="Inscrição Estadual"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="formData.user.receiverStateTaxIndicator"
                :items="taxIndicators"
                label="Indicador de Inscrição Estadual"
                outlined
                dense
              ></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.user.municipalTaxNumber"
                label="Inscrição Municipal"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Dados de Endereço -->
          <h3 class="text-h6 mb-3">Dados de Endereço</h3>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field
                v-model="formData.address.street"
                label="Logradouro *"
                :rules="rules.required"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.address.apartmentNumber"
                label="Número do Apartamento"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.address.residentialNumber"
                label="Número Residencial"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.address.complement"
                label="Complemento"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.address.district"
                label="Bairro *"
                :rules="rules.required"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.address.city"
                label="Cidade *"
                :rules="rules.required"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="formData.address.state"
                :items="states"
                label="Estado *"
                :rules="rules.required"
                outlined
                dense
              ></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.address.zipCode"
                label="CEP *"
                :rules="rules.cep"
                v-mask="'##.###-###'"
                outlined
                dense
                @blur="searchZipCode"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.address.country"
                label="País *"
                :rules="rules.required"
                outlined
                dense
                value="Brasil"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.address.ibgeCityCode"
                label="Código IBGE da Cidade"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          text
          @click="close"
          :disabled="loading"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          @click="confirm"
          :disabled="!valid || loading"
          :loading="loading"
        >
          Confirmar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Rules from '@/utils/Rules';

export default {
  name: 'BillingOrderDialog',
  props: {
    openModal: {
      type: Boolean,
      default: false
    },
    proposal: {
      type: Object,
      default: null
    },
    leadData: {
      type: Object,
      default: null
    },
    total: {
      type: [String, Number],
      default: '0'
    }
  },
  data() {
    return {
      valid: false,
      loading: false,
      isUpdatingPaymentMethod: false, // Flag para evitar loops no watch
      rules: {
        required: Rules.required(),
        email: Rules.email(),
        cep: Rules.string(true, 10),
        paymentMethodRequired: (v) => {
          if (!v || (Array.isArray(v) && v.length === 0)) {
            return 'Selecione pelo menos uma forma de pagamento';
          }
          return true;
        },
        entryPercentage: (v) => {
          // Só validar se for parcelado e tipo de entrada for percentual
          if (this.formData.billingType !== 'installments' || this.formData.entryType !== 'percentage') {
            return true;
          }
          if (!v || v === '' || v === null || v === undefined) {
            return 'Informe o percentual de entrada';
          }
          const percentage = parseFloat(v);
          if (isNaN(percentage) || percentage < 0 || percentage > 100) {
            return 'Percentual deve estar entre 0 e 100';
          }
          return true;
        },
        entryValue: (v) => {
          // Só validar se for parcelado e tipo de entrada for valor
          if (this.formData.billingType !== 'installments' || this.formData.entryType !== 'value') {
            return true;
          }
          if (!v || v === '' || v === null || v === undefined) {
            return 'Informe o valor da entrada';
          }
          const value = parseFloat(v);
          const total = parseFloat(this.formData.total || 0);
          if (isNaN(value) || value < 0) {
            return 'Valor da entrada deve ser maior ou igual a zero';
          }
          if (value > total) {
            return 'Valor da entrada não pode ser maior que o total';
          }
          return true;
        },
        numberOfInstallments: (v) => {
          // Só validar se for parcelado
          if (this.formData.billingType !== 'installments') {
            return true;
          }
          if (!v || v === '' || v === null || v === undefined) {
            return 'Informe o número de parcelas';
          }
          const num = parseInt(v);
          if (isNaN(num) || num < 1) {
            return 'Número de parcelas deve ser maior ou igual a 1';
          }
          // Se houver entrada, só permite 1 parcela
          if (this.formData.entryType && this.formData.entryType !== 'none' && num !== 1) {
            return 'Com entrada, apenas 1 parcela é permitida';
          }
          return true;
        }
      },
      paymentMethods: [
        { text: 'Boleto', value: 'bank_slip' },
        { text: 'Cartão de Crédito', value: 'credit_card' },
        { text: 'PIX', value: 'pix' },
        { text: 'Todas', value: 'all' }
      ],
      billingTypes: [
        { text: 'Fatura Única', value: 'single' },
        { text: 'Parcelado', value: 'installments' }
      ],
      entryTypes: [
        { text: 'Percentual', value: 'percentage' },
        { text: 'Valor Fixo', value: 'value' },
        { text: 'Sem Entrada', value: 'none' }
      ],
      userTypes: [
        { text: 'Pessoa Física', value: 'physical' },
        { text: 'Pessoa Jurídica', value: 'legal' }
      ],
      taxIndicators: [
        { text: 'Nenhum', value: 'None' },
        { text: 'Contribuinte', value: 'TaxPayer' },
        { text: 'Isento', value: 'Exempt' },
        { text: 'Não Contribuinte', value: 'NonTaxPayer' }
      ],
      states: [
        'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
        'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
      ],
      formData: {
        paymentMethod: [],
        billingType: 'single',
        dueDate: null,
        total: '',
        entryType: 'percentage',
        entryPercentage: 30,
        entryValue: '',
        numberOfInstallments: 2,
        installments: [],
        user: {
          name: '',
          email: '',
          cpf: '',
          cnpj: '',
          nameResponsible: '',
          cpfResponsible: '',
          emailResponsible: '',
          phoneResponsible: '',
          userType: null,
          stateTaxNumber: null,
          receiverStateTaxIndicator: null,
          municipalTaxNumber: null
        },
        address: {
          street: '',
          city: '',
          district: '',
          state: '',
          apartmentNumber: '',
          zipCode: '',
          residentialNumber: null,
          complement: null,
          country: 'Brasil',
          ibgeCityCode: ''
        }
      }
    };
  },
  watch: {
    openModal(newValue) {
      if (newValue) {
        this.initForm();
        // Forçar revalidação após inicializar o formulário
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.$refs.form.validate();
          }
        });
      }
    },
    'formData.paymentMethod': {
      handler(newValue) {
        if (!Array.isArray(newValue) || this.isUpdatingPaymentMethod) return;
        
        const hasAll = newValue.includes('all');
        const requiredMethods = ['bank_slip', 'credit_card', 'pix'];
        const hasAllThree = requiredMethods.every(method => newValue.includes(method));
        
        // Se "all" foi selecionado e não temos todas as três opções individuais
        if (hasAll && !hasAllThree) {
          // Marcar flag para evitar loop
          this.isUpdatingPaymentMethod = true;
          // Adicionar todas as opções individuais mantendo "all"
          const updated = [...new Set([...requiredMethods, 'all'])];
          this.$nextTick(() => {
            this.formData.paymentMethod = updated;
            this.$nextTick(() => {
              this.isUpdatingPaymentMethod = false;
            });
          });
        }
      },
      deep: true
    },
    'formData.user.userType': {
      handler(newValue) {
        // Limpar CPF quando mudar para Pessoa Jurídica
        if (newValue === 'legal') {
          this.formData.user.cpf = '';
        }
        // Limpar CNPJ quando mudar para Pessoa Física
        if (newValue === 'physical') {
          this.formData.user.cnpj = '';
        }
        // Forçar revalidação do formulário
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.$refs.form.validate();
          }
        });
      }
    },
    leadData: {
      handler(newValue) {
        if (newValue && this.openModal) {
          this.populateFromLead(newValue);
        }
      },
      immediate: true
    },
    total: {
      handler(newValue) {
        if (newValue) {
          this.formData.total = String(newValue);
        }
      },
      immediate: true
    },
    'formData.installments': {
      handler() {
        // Forçar revalidação quando parcelas mudam
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.$refs.form.validate();
          }
        });
      },
      deep: true
    },
    'formData.entryType': {
      handler(newValue) {
        // Se houver entrada, garantir que seja apenas 1 parcela
        if (newValue && newValue !== 'none' && this.formData.billingType === 'installments') {
          if (this.formData.numberOfInstallments !== 1) {
            this.formData.numberOfInstallments = 1;
            this.generateInstallments();
          }
        }
      }
    }
  },
  computed: {
    calculatedEntry() {
      if (this.formData.billingType !== 'installments') return 0;
      if (this.formData.entryType === 'none') return 0;
      
      const total = parseFloat(this.formData.total || 0);
      if (isNaN(total) || total <= 0) return 0;

      if (this.formData.entryType === 'percentage') {
        const percentage = parseFloat(this.formData.entryPercentage || 0);
        return (total * percentage) / 100;
      } else if (this.formData.entryType === 'value') {
        return parseFloat(this.formData.entryValue || 0);
      }
      
      return 0;
    },
    remainingValue() {
      if (this.formData.billingType !== 'installments') return 0;
      
      const total = parseFloat(this.formData.total || 0);
      return total - this.calculatedEntry;
    }
  },
  methods: {
    initForm() {
      // Reset form
      this.formData = {
        paymentMethod: [],
        billingType: 'single',
        dueDate: null,
        total: String(this.total || '0'),
        entryType: 'percentage',
        entryPercentage: 30,
        entryValue: '',
        numberOfInstallments: 2,
        installments: [],
        user: {
          name: '',
          email: '',
          cpf: '',
          cnpj: '',
          nameResponsible: '',
          cpfResponsible: '',
          emailResponsible: '',
          phoneResponsible: '',
          userType: null,
          stateTaxNumber: null,
          receiverStateTaxIndicator: null,
          municipalTaxNumber: null
        },
        address: {
          street: '',
          city: '',
          district: '',
          state: '',
          apartmentNumber: '',
          zipCode: '',
          residentialNumber: null,
          complement: null,
          country: 'Brasil',
          ibgeCityCode: ''
        }
      };

      // Preencher com dados do lead se disponível
      if (this.leadData) {
        this.populateFromLead(this.leadData);
      }

      // Preencher total da proposta
      if (this.total) {
        this.formData.total = String(this.total);
      }

      // Definir data de vencimento padrão (30 dias a partir de hoje)
      const today = new Date();
      const dueDate = new Date(today);
      dueDate.setDate(today.getDate() + 30);
      this.formData.dueDate = dueDate.toISOString().split('T')[0];
    },
    populateFromLead(lead) {
      console.log("lead", lead)
      if (!lead) return;

      // Dados do usuário
      if (lead.name) this.formData.user.name = lead.name;
      if (lead.email) this.formData.user.email = lead.email;
      if (lead.document) {
        // Verificar se é CNPJ ou CPF
        const doc = lead.document.replace(/\D/g, '');
        if (doc.length === 14) {
          this.formData.user.cnpj = lead.document;
          this.formData.user.userType = 'legal';
        } else if (doc.length === 11) {
          this.formData.user.cpf = lead.document;
          this.formData.user.userType = 'physical';
        }
      }
      if (lead.responsible_name) this.formData.user.nameResponsible = lead.responsible_name;
      if (lead.responsible_document) {
        const doc = lead.responsible_document.replace(/\D/g, '');
        if (doc.length === 11) {
          this.formData.user.cpfResponsible = lead.responsible_document;
        }
      }
      if (lead.responsible_email) this.formData.user.emailResponsible = lead.responsible_email;
      if (lead.responsible_phone) this.formData.user.phoneResponsible = lead.responsible_phone;

      // Dados de endereço - preencher todos os campos disponíveis
      if (lead.address) this.formData.address.street = lead.address;
      if (lead.house_number) this.formData.address.residentialNumber = lead.house_number;
      if (lead.address_complement) this.formData.address.complement = lead.address_complement;
      if (lead.district) this.formData.address.district = lead.district;
      if (lead.city) this.formData.address.city = lead.city;
      if (lead.uf) this.formData.address.state = lead.uf;
      if (lead.zip_code) {
        // Formatar CEP se necessário (adicionar hífen se não tiver)
        let zipCode = lead.zip_code.replace(/\D/g, '');
        if (zipCode.length === 8) {
          zipCode = zipCode.replace(/(\d{5})(\d{3})/, '$1-$2');
        }
        this.formData.address.zipCode = zipCode;
      }
      
      // Campos adicionais que podem existir no lead
      if (lead.apartment_number) this.formData.address.apartmentNumber = lead.apartment_number;
      if (lead.country) this.formData.address.country = lead.country;
      if (lead.ibge_city_code) this.formData.address.ibgeCityCode = lead.ibge_city_code;
      
      // Garantir que o país seja Brasil se não estiver definido
      if (!this.formData.address.country) {
        this.formData.address.country = 'Brasil';
      }
      
      // Forçar revalidação após preencher dados do lead
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.validate();
        }
      });
    },
    async searchZipCode() {
      const zipCode = this.formData.address.zipCode?.replace(/\D/g, '');
      if (zipCode && zipCode.length === 8) {
        try {
          // Aqui você pode integrar com uma API de CEP se necessário
          // Por enquanto, apenas validamos o formato
        } catch (error) {
          console.error('Erro ao buscar CEP:', error);
        }
      }
    },
    formatCurrency(value) {
      if (!value) return 'R$ 0,00';
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue)) return 'R$ 0,00';
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(numValue);
    },
    onBillingTypeChange() {
      if (this.formData.billingType === 'single') {
        // Limpar dados de parcelamento
        this.formData.installments = [];
        this.formData.entryType = 'percentage';
        this.formData.entryPercentage = 30;
        this.formData.entryValue = '';
        this.formData.numberOfInstallments = 2;
      } else if (this.formData.billingType === 'installments') {
        // Se houver entrada, garantir que seja apenas 1 parcela
        if (this.formData.entryType && this.formData.entryType !== 'none') {
          this.formData.numberOfInstallments = 1;
        }
        // Gerar parcelas iniciais
        this.generateInstallments();
      }
      // Forçar revalidação do formulário
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.validate();
        }
      });
    },
    calculateInstallments() {
      if (this.formData.billingType !== 'installments') return;
      
      const total = parseFloat(this.formData.total || 0);
      if (isNaN(total) || total <= 0) return;

      let entryValue = 0;
      
      if (this.formData.entryType === 'percentage') {
        const percentage = parseFloat(this.formData.entryPercentage || 0);
        entryValue = (total * percentage) / 100;
      } else if (this.formData.entryType === 'value') {
        entryValue = parseFloat(this.formData.entryValue || 0);
      }

      const remainingValue = total - entryValue;
      const numberOfInstallments = parseInt(this.formData.numberOfInstallments || 0);
      
      if (numberOfInstallments > 0 && this.formData.installments.length > 0) {
        // Atualizar entrada se houver
        if (this.formData.entryType !== 'none' && entryValue > 0 && this.formData.installments.length > 0) {
          const entryIndex = this.formData.installments.findIndex(inst => inst.type === 'entry');
          if (entryIndex >= 0) {
            this.formData.installments[entryIndex].value = entryValue;
          }
        }
        
        // Calcular valor de cada parcela (dividir o restante igualmente)
        const installmentValue = remainingValue / numberOfInstallments;
        
        // Atualizar valores das parcelas (exceto entrada)
        this.formData.installments.forEach((inst, index) => {
          if (inst.type !== 'entry') {
            inst.value = installmentValue;
          }
        });
      }
    },
    generateInstallments() {
      if (this.formData.billingType !== 'installments') return;
      
      // Se houver entrada, garantir que seja apenas 1 parcela
      if (this.formData.entryType && this.formData.entryType !== 'none') {
        this.formData.numberOfInstallments = 1;
      }
      
      const total = parseFloat(this.formData.total || 0);
      const numberOfInstallments = parseInt(this.formData.numberOfInstallments || 0);
      
      if (isNaN(total) || total <= 0 || numberOfInstallments < 1) {
        this.formData.installments = [];
        return;
      }

      let entryValue = 0;
      
      if (this.formData.entryType === 'percentage') {
        const percentage = parseFloat(this.formData.entryPercentage || 0);
        entryValue = (total * percentage) / 100;
      } else if (this.formData.entryType === 'value') {
        entryValue = parseFloat(this.formData.entryValue || 0);
      }

      const remainingValue = total - entryValue;
      const installmentValue = numberOfInstallments > 0 ? remainingValue / numberOfInstallments : 0;

      // Criar array de parcelas
      this.formData.installments = [];
      
      // Adicionar entrada se houver
      if (entryValue > 0 && this.formData.entryType !== 'none') {
        const today = new Date();
        this.formData.installments.push({
          value: entryValue,
          dueDate: today.toISOString().split('T')[0],
          type: 'entry'
        });
      }
      
      // Adicionar parcelas
      const today = new Date();
      const startMonth = entryValue > 0 && this.formData.entryType !== 'none' ? 1 : 0;
      for (let i = 1; i <= numberOfInstallments; i++) {
        const dueDate = new Date(today);
        dueDate.setMonth(today.getMonth() + startMonth + i);
        this.formData.installments.push({
          value: installmentValue,
          dueDate: dueDate.toISOString().split('T')[0],
          type: 'installment',
          number: i
        });
      }
      
      // Forçar revalidação do formulário após gerar parcelas
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.validate();
        }
      });
    },
    onEntryTypeChange() {
      // Se houver entrada, garantir que seja apenas 1 parcela
      if (this.formData.entryType && this.formData.entryType !== 'none') {
        this.formData.numberOfInstallments = 1;
      }
      this.generateInstallments();
    },
    onNumberOfInstallmentsChange() {
      // Se houver entrada, forçar para 1 parcela
      if (this.formData.entryType && this.formData.entryType !== 'none') {
        if (this.formData.numberOfInstallments !== 1) {
          this.formData.numberOfInstallments = 1;
        }
      }
      this.generateInstallments();
    },
    removeInstallment(index) {
      const installment = this.formData.installments[index];
      if (installment && installment.type === 'entry') {
        // Não permitir remover entrada
        return;
      }
      // Não permitir remover se houver entrada (só pode ter 1 parcela)
      if (this.formData.entryType && this.formData.entryType !== 'none') {
        return;
      }
      this.formData.installments.splice(index, 1);
      // Recalcular número de parcelas (excluindo entrada se houver)
      const installmentsOnly = this.formData.installments.filter(inst => inst.type !== 'entry');
      this.formData.numberOfInstallments = Math.max(1, installmentsOnly.length);
      this.calculateInstallments();
    },
    close() {
      this.$emit('close');
    },
    confirm() {
      if (this.$refs.form.validate()) {
        // Validação adicional para parcelas
        if (this.formData.billingType === 'installments') {
          if (!this.formData.installments || this.formData.installments.length === 0) {
            alert('Configure pelo menos uma parcela');
            return;
          }
          // Validar que todas as parcelas têm vencimento
          const hasInvalidInstallment = this.formData.installments.some(
            inst => !inst.dueDate || !inst.value || inst.value <= 0
          );
          if (hasInvalidInstallment) {
            alert('Todas as parcelas devem ter valor e data de vencimento válidos');
            return;
          }
        } else {
          // Validação para fatura única
          if (!this.formData.dueDate) {
            alert('Informe a data de vencimento');
            return;
          }
        }

        const billingData = {
          paymentMethod: this.formData.paymentMethod,
          billingType: this.formData.billingType,
          dueDate: this.formData.billingType === 'single' ? String(this.formData.dueDate) : null,
          total: String(this.formData.total),
          user: {
            ...this.formData.user,
            // Remover campos null/undefined
            stateTaxNumber: this.formData.user.stateTaxNumber || null,
            receiverStateTaxIndicator: this.formData.user.receiverStateTaxIndicator || null,
            municipalTaxNumber: this.formData.user.municipalTaxNumber || null
          },
          address: {
            ...this.formData.address,
            residentialNumber: this.formData.address.residentialNumber || null,
            complement: this.formData.address.complement || null
          }
        };

        // Adicionar dados de parcelamento se for parcelado
        if (this.formData.billingType === 'installments') {
          billingData.entryType = this.formData.entryType;
          billingData.entryPercentage = this.formData.entryType === 'percentage' ? this.formData.entryPercentage : null;
          billingData.entryValue = this.formData.entryType === 'value' ? this.formData.entryValue : null;
          billingData.numberOfInstallments = this.formData.numberOfInstallments;
          billingData.installments = this.formData.installments.map(inst => ({
            value: String(inst.value),
            dueDate: String(inst.dueDate),
            type: inst.type,
            number: inst.number || null
          }));
        }

        this.$emit('confirm', billingData);
      }
    }
  }
};
</script>

<style scoped>
.v-card-title {
  display: flex;
  align-items: center;
}
</style>

